import { cn } from '@/lib/utils';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.apetech.studio';

interface Props {
  /** Email or any identifier — used for the fallback initial + stable hue. */
  email: string | null;
  /** R2 object key. Wins over avatar_url. */
  image_key?: string | null;
  /** External URL fallback. */
  avatar_url?: string | null;
  /** Pixel size (square). Default 24. */
  size?: number;
  /** Optional outer className for extra rings / shadows. */
  className?: string;
  /** Tooltip text. Defaults to the email. */
  title?: string;
}

/**
 * Single source of truth for rendering a user's avatar. Image precedence:
 *   1. uploaded image_key (resolved via /v1/files/<key>)
 *   2. external avatar_url
 *   3. email-initial circle with a stable hue per email
 *
 * Cache-buster on the file URL so a freshly uploaded avatar invalidates
 * the previous cache without waiting on the immutable cache-control TTL.
 */
export function UserAvatar({
  email,
  image_key,
  avatar_url,
  size = 24,
  className,
  title,
}: Props) {
  const tooltip = title ?? email ?? '';
  const initial = (email ?? '?')[0]?.toUpperCase() ?? '?';
  const hue = email ? stringToHue(email) : 0;
  const px = `${size}px`;

  if (image_key) {
    return (
      <img
        src={`${API_BASE}/v1/files/${image_key}?v=${encodeURIComponent(image_key)}`}
        alt=""
        width={size}
        height={size}
        title={tooltip}
        className={cn('block shrink-0 rounded-full object-cover', className)}
        style={{ width: px, height: px }}
      />
    );
  }
  if (avatar_url) {
    return (
      <img
        src={avatar_url}
        alt=""
        width={size}
        height={size}
        title={tooltip}
        className={cn('block shrink-0 rounded-full object-cover', className)}
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <span
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-full font-semibold text-secondary-foreground',
        className,
      )}
      style={{
        width: px,
        height: px,
        background: `oklch(0.42 0.06 ${hue})`,
        fontSize: `${Math.round(size * 0.42)}px`,
      }}
    >
      {initial}
    </span>
  );
}

function stringToHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}
